import React, { useEffect, useRef, useState } from 'react';
import { files as filesApi } from '../services/api';

/**
 * AppImage — one shared image container for card thumbnails.
 *
 * • Fixed 16:9 aspect (`overflow-hidden`).
 * • `object-fit: cover` + `object-position: center`.
 * • Subtle bottom-fade gradient overlay (opt-out via `overlay={false}`).
 * • Blur-up LQIP while the sharp image loads.
 * • Server-side thumbnail variant selection (400px for cards, 800px for
 *   detail pages).
 * • When `src` is missing/empty, renders a subtle neutral tile — NEVER
 *   swaps in a branded placeholder image over an uploaded photo.
 * • IntersectionObserver lazy-mount so long grids don't preload every
 *   image at once.
 */
export default function AppImage({
  src,
  alt = '',
  size = 'card',                     // 'card' | 'detail'
  aspect = '16/9',
  rounded = 'xl',                    // 'lg' | 'xl' | '2xl' | 'top' | 'none'
  overlay = true,
  lqip: lqipProp = null,
  className = '',
  imgClassName = '',
  loading = 'lazy',
  fallbackSrc = null,
  fallback,
  onLoad,
  ...rest
}) {
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [lqip, setLqip] = useState(lqipProp);
  const lqipFetched = useRef(false);
  const ioRef = useRef(null);
  const containerRef = useRef(null);
  const [inView, setInView] = useState(false);

  const targetWidth = size === 'detail' ? 800 : 400;
  const targetSrc = filesApi.variantUrl(src, targetWidth);
  const resolved = filesApi.urlFor(targetSrc);
  const hasImage = !!resolved && !errored;

  useEffect(() => {
    if (!containerRef.current || inView) return;
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    ioRef.current = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { setInView(true); ioRef.current?.disconnect(); } });
    }, { rootMargin: '400px 0px' });
    ioRef.current.observe(containerRef.current);
    return () => ioRef.current?.disconnect();
  }, [inView]);

  useEffect(() => {
    if (lqip || !src || lqipFetched.current || !inView) return;
    if (!src.startsWith('/api/') && !src.startsWith('http')) return;
    if (src.startsWith('data:')) return;
    const looksLikeUpload = /\/api\/files\//.test(src) ||
                            (src.startsWith('/api/') && /\.(?:png|jpe?g|webp|gif)$/i.test(src));
    if (!looksLikeUpload) return;
    lqipFetched.current = true;
    filesApi.lqipFor(src)
      .then(r => { if (r?.lqip) setLqip(r.lqip); })
      .catch(() => {});
  }, [src, lqip, inView]);

  useEffect(() => {
    setErrored(false);
    setLoaded(false);
    if (!lqipProp) { setLqip(null); lqipFetched.current = false; }
  }, [src, lqipProp]);

  const roundedClass =
    rounded === 'none' ? '' :
    rounded === 'top'  ? 'rounded-t-xl' :
    rounded === 'lg'   ? 'rounded-lg' :
    rounded === '2xl'  ? 'rounded-2xl' :
    'rounded-xl';

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-hidden bg-[#0d1f14] ${roundedClass} ${className}`}
      style={{ aspectRatio: aspect }}
      data-testid="app-image"
    >
      {lqip && hasImage && !loaded && (
        <img
          src={lqip}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center scale-110 blur-xl"
          data-testid="app-image-lqip"
        />
      )}
      {inView && hasImage && (
        <img
          src={resolved}
          alt={alt}
          loading={loading}
          decoding="async"
          onError={() => setErrored(true)}
          onLoad={(e) => { setLoaded(true); onLoad?.(e); }}
          className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'} ${imgClassName}`}
          {...rest}
        />
      )}
      {!hasImage && fallbackSrc && (
        <div className="absolute inset-0 bg-white flex items-center justify-center p-3" data-testid="app-image-fallback">
          <img src={fallbackSrc} alt={alt} className="max-w-full max-h-full object-contain"/>
        </div>
      )}
      {overlay && hasImage && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 via-black/15 to-transparent"
          aria-hidden="true"
        />
      )}
    </div>
  );
}
